import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@material-ui/core';
import { OpenInNew as OpenInNewIcon } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

import { isVersionEqual } from '../../../common/version-item/Version.helpers';
import { VersionItem } from '../../../common/version-item/Version.interface';
import { formatDate } from '../../../common/formatter';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useFindFileVersions } from '../Document.hook';
import { FileDto } from '../Document.interface';

const StyledDialogContent = styled(DialogContent)`
  padding: 8px 0 0;
  .pagination {
    bottom: 0;
    position: sticky;
    background-color: #fff;
  }
`;

const columns: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'version', label: 'Version' },
  { key: 'modified', label: 'Last Modified' },
  { key: 'status', label: 'Status' }
];

export interface DocumentVersionDialogPickerProps {
  file: FileDto;
  open: boolean;
  multiple?: boolean;
  items?: VersionItem[];
  onCancel?: () => void;
  onSelect?: (items: VersionItem[]) => void;
}

const DocumentVersionDialogPicker: React.FC<
  DocumentVersionDialogPickerProps
> = props => {
  const { open, file, items, multiple, onCancel, onSelect } = props;
  const [{ data, isLoading, error }, findVersions] = useFindFileVersions(
    file.id
  );
  const defaultLimit = 5;
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [order, setOrder] = useState<string>();
  const [direction, setDirection] = useState<'ASC' | 'DESC'>();
  const [didChange, setDidChange] = useState(false);
  const [selected, setSelected] = useState<VersionItem[]>([]);
  const colSpan = 6;

  const isSelected = useCallback(
    (item: VersionItem) => selected.some(s => isVersionEqual(s, item)),
    [selected]
  );

  const files = useMemo(() => {
    return (data?.data || []).filter(item => !isSelected(item));
  }, [data, isSelected]);

  const isActive = (column: string) => column === order;

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return isActive(column) && direction === 'DESC' ? 'desc' : 'asc';
  };

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  // clear state
  useEffect(() => {
    if (!open) {
      setPage(0);
      setLimit(defaultLimit);
      setOrder(undefined);
      setDirection(undefined);
      setDidChange(false);
      setSelected([]);
    }
  }, [open]);

  // set default selected items, skip when items are changed
  useEffect(() => {
    if (didChange || !(open && items)) {
      return;
    }
    const selected = items.filter(item => item.id === file.id);
    setSelected(multiple ? selected : selected.slice(-1));
  }, [file.id, open, items, multiple, didChange]);

  useEffect(() => {
    findVersions({ page, limit, order, direction });
  }, [open, page, limit, order, direction, findVersions]);

  const renderRows = (items: VersionItem[], checked = false) => {
    return items.map((item, index) => {
      const params = item.draft ? '' : `?ver=${item.version}`;
      const url = `/portal/documents/file/task/${item.taskId}${params}`;
      return (
        <TableRow
          hover
          selected={checked}
          key={`${item.id}-${item.version}-${index}`}
          onClick={() => {
            setDidChange(true);
            setSelected(items => {
              if (!checked) {
                return multiple ? items.concat(item) : [item];
              }
              return items.filter(selected => !isVersionEqual(selected, item));
            });
          }}
        >
          <TableCell padding='checkbox'>
            <Checkbox color='primary' checked={checked} />
          </TableCell>
          <WrappedTableCell>{item.name}</WrappedTableCell>
          <TableCell>{item.version}</TableCell>
          <WrappedTableCell>{formatDate(item.task.modified)}</WrappedTableCell>
          <TableCell>
            <TaskStatusLabel task={item.task} />
          </TableCell>
          <TableCell padding='checkbox'>
            <Tooltip arrow placement='top' title='View Document Version'>
              <Link href={url} target='_blank'>
                <IconButton
                  color='inherit'
                  onClick={event => event.stopPropagation()}
                >
                  <OpenInNewIcon />
                </IconButton>
              </Link>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='md'>
      <DialogTitle>Choose Document Version</DialogTitle>
      <StyledDialogContent>
        <Box mx={3}>
          <DialogContentText>
            Select from available versions of{' '}
            <strong>{file.task?.key || `DOC=${file.id}`}</strong>.
          </DialogContentText>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                {columns.map((column, index) => (
                  <TableCell key={`${column.key}-${index}`}>
                    <TableSortLabel
                      active={isActive(column.key)}
                      direction={getSortDirection(column.key)}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell padding='none' colSpan={colSpan}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {error && (
                <TableRow>
                  <TableCell padding='none' colSpan={colSpan}>
                    <Alert severity='error'>{error.message}</Alert>
                  </TableCell>
                </TableRow>
              )}
              {(!data || data.data.length === 0) && (
                <TableRow>
                  <TableCell colSpan={colSpan}>
                    <Typography variant='body1'>
                      {isLoading
                        ? 'Loading versions...'
                        : 'No available versions.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {renderRows(selected, true)}
              {renderRows(files)}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          className='pagination'
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={data?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, page) => setPage(page)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={isLoading}
          onClick={() => onSelect?.(selected)}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentVersionDialogPicker;
